import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Bot, CalendarCheck, Inbox, TrendingUp, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import CRMJourney from './CRMJourney';
import { scrollToSection } from '../../utils/scroll';

/* ──────────────────────────────────────────────────────────────────────────
   CRMExplanation — Problema → Solución → Proceso.
   Antes era un scroll horizontal "pineado" de 1080vh; ahora son tres secciones
   verticales apiladas que aparecen al bajar, en línea con el resto de la página.
   ────────────────────────────────────────────────────────────────────────── */

const CRMExplanation: React.FC = () => {
  const { t } = useTranslation();
  const [showCrmDef, setShowCrmDef] = useState(false);
  const [isLight, setIsLight] = useState(false);

  /* Qué obtiene el cliente — beneficios concretos, no jerga. */
  const CRM_BENEFITS = [
    { icon: Bot,           label: t('crmdef.benefit1_label', 'Respondes 24/7'),   sub: t('crmdef.benefit1_sub', 'Un agente de IA contesta cada mensaje al instante, aunque estés durmiendo.') },
    { icon: CalendarCheck, label: t('crmdef.benefit2_label', 'Agenda llena'),     sub: t('crmdef.benefit2_sub', 'Reserva citas y envía recordatorios solo, sin que muevas un dedo.') },
    { icon: Inbox,         label: t('crmdef.benefit3_label', 'Todo en un lugar'), sub: t('crmdef.benefit3_sub', 'Todos tus chats de WhatsApp ordenados en una sola bandeja.') },
    { icon: TrendingUp,    label: t('crmdef.benefit4_label', 'Más ventas'),       sub: t('crmdef.benefit4_sub', 'Ningún cliente se enfría: el sistema da seguimiento por ti.') },
  ];

  useEffect(() => {
    const checkTheme = () => {
      setIsLight(document.documentElement.getAttribute('data-theme') === 'light');
    };

    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div
      id="nosotros"
      style={{
        position: 'relative',
        background: 'transparent',
      }}
    >
      <CRMJourney isLight={isLight} setShowCrmDef={setShowCrmDef} />

      <style>{`
        .grad-text {
          background: linear-gradient(135deg, #06B6D4 0%, #7C3AED 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        /* Prueba: mismo tratamiento que el titular del Hero — blanco/negro
           sólido (hereda var(--text-primary)) en vez del gradiente teal/púrpura. */
        .journey-text-column .grad-text {
          background: none;
          -webkit-text-fill-color: inherit;
          color: inherit;
        }

        /* Mismo lineamiento que el botón del Hero (.trial-btn): pill, sin
           movimiento, colores invertidos por tema para no perderse contra
           el fondo negro puro del tema oscuro. */
        .btn-crm-info {
          background: #fff;
          color: #000;
          border: none;
          border-radius: 980px;
          font-family: var(--font-body);
          transition: background 0.2s ease;
        }

        .btn-crm-info:hover {
          background: #e8e8ed;
        }

        [data-theme='light'] .btn-crm-info {
          background: #000;
          color: #fff;
        }

        [data-theme='light'] .btn-crm-info:hover {
          background: #1d1d1f;
        }

        .crm-panel {
          width: 100%;
          display: flex;
          align-items: center;
          padding: clamp(4rem, 8vh, 7rem) 0;
        }

        .journey-container {
          width: 100%;
          max-width: 1480px;
          padding-left: clamp(2rem, 5vw, 5rem);
          padding-right: clamp(2rem, 5vw, 5rem);
        }

        .journey-layout-grid {
          width: 100%;
          display: grid;
          grid-template-columns: minmax(260px, 0.42fr) minmax(0, 1.58fr);
          gap: clamp(1.25rem, 2vw, 2.5rem);
          align-items: center;
        }

        .journey-text-column {
          width: 100%;
          max-width: 390px;
        }

        .journey-board-column {
          position: relative;
          width: 100%;
          height: min(700px, calc(100vh - 110px));
          min-height: 560px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @media (max-width: 1366px) {
          .journey-container {
            max-width: 1366px;
            padding-left: clamp(1.5rem, 4vw, 4rem);
            padding-right: clamp(1.5rem, 4vw, 4rem);
          }

          .journey-layout-grid {
            grid-template-columns: minmax(250px, 0.45fr) minmax(0, 1.55fr);
            gap: 1.75rem;
          }

          .journey-text-column {
            max-width: 370px;
          }

          .journey-board-column {
            height: min(660px, calc(100vh - 115px));
            min-height: 520px;
          }
        }

        @media (max-width: 1280px) {
          .journey-container {
            max-width: 1280px;
            padding-left: clamp(1.25rem, 3.5vw, 3.5rem);
            padding-right: clamp(1.25rem, 3.5vw, 3.5rem);
          }

          .journey-layout-grid {
            grid-template-columns: minmax(245px, 0.5fr) minmax(0, 1.5fr);
            gap: 1.5rem;
          }

          .journey-text-column {
            max-width: 360px;
          }

          .journey-board-column {
            height: min(620px, calc(100vh - 120px));
            min-height: 490px;
          }
        }

        @media (max-width: 1024px) {
          .journey-container {
            padding-left: 2rem;
            padding-right: 2rem;
          }

          .journey-layout-grid {
            grid-template-columns: 1fr;
            gap: 3rem;
          }

          .journey-text-column {
            max-width: 100%;
          }

          .journey-board-column {
            height: 420px;
            min-height: 380px;
          }
        }

        @media (max-width: 640px) {
          .journey-container {
            padding-left: 1.25rem;
            padding-right: 1.25rem;
          }

          .journey-board-column {
            height: 320px;
            min-height: 280px;
          }
        }
      `}</style>

      <AnimatePresence>
        {showCrmDef && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem',
            }}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowCrmDef(false)}
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(3, 7, 18, 0.85)',
                backdropFilter: 'blur(15px)',
              }}
            />

            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              style={{
                position: 'relative',
                width: '100%',
                maxWidth: '640px',
                background: isLight ? '#f1f5f9' : '#0a0a0a',
                borderRadius: '32px',
                padding: '3rem 2.5rem',
                boxShadow: '0 30px 60px rgba(0,0,0,0.5)',
                zIndex: 1,
                overflow: 'hidden',
                border: isLight
                  ? '1px solid rgba(0,0,0,0.05)'
                  : '1px solid rgba(255,255,255,0.05)',
              }}
            >
              <button
                onClick={() => setShowCrmDef(false)}
                aria-label={t('crmdef.close', 'Cerrar')}
                style={{
                  position: 'absolute',
                  top: '24px',
                  right: '24px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                }}
              >
                <X size={18} />
              </button>

              <div style={{ position: 'relative' }}>
                <span style={{ display: 'inline-block', fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.9rem' }}>
                  {t('crmdef.eyebrow', 'En palabras simples')}
                </span>
                <h3
                  style={{
                    fontSize: '1.9rem',
                    fontWeight: 800,
                    marginBottom: '1.1rem',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.15,
                    color: 'var(--text-primary)',
                  }}
                >
                  {t('crmdef.title1', 'Un')}{' '}
                  <span style={{ fontWeight: 800 }}>
                    CRM
                  </span>{' '}
                  {t('crmdef.title2', 'es el cerebro de tu negocio.')}
                </h3>

                <p
                  style={{
                    color: isLight ? 'var(--text-secondary)' : 'rgba(255,255,255,0.78)',
                    lineHeight: 1.65,
                    fontSize: '1rem',
                    margin: '0 0 1.6rem',
                  }}
                >
                  {t('crmdef.desc1', 'Reúne')} <span style={{ color: isLight ? 'var(--text-primary)' : '#fff', fontWeight: 600 }}>{t('crmdef.desc2', 'cada chat, cada cliente y cada venta')}</span> {t('crmdef.desc3', 'en un solo lugar y los pone a trabajar por ti. RurayHUB lo lleva un paso más allá: le suma un')}{' '}
                  <span style={{ color: isLight ? 'var(--text-primary)' : '#fff', fontWeight: 600 }}>{t('crmdef.desc4', 'agente de IA')}</span> {t('crmdef.desc5', 'que atiende y vende solo.')}
                </p>

                <div
                  style={{
                    borderTop: isLight ? '1px solid rgba(0,0,0,0.07)' : '1px solid rgba(255,255,255,0.07)',
                    paddingTop: '1.4rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                  }}
                >
                  {CRM_BENEFITS.map(({ icon: Icon, label, sub }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'flex-start', gap: 13 }}>
                      <span style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.08)', border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.14)' }}>
                        <Icon size={17} color="var(--text-primary)" />
                      </span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.96rem', color: 'var(--text-primary)', lineHeight: 1.2, marginBottom: 2 }}>{label}</div>
                        <div style={{ fontSize: '0.86rem', color: isLight ? 'var(--text-secondary)' : 'rgba(255,255,255,0.62)', lineHeight: 1.45 }}>{sub}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => { setShowCrmDef(false); scrollToSection('#cta-final'); }}
                  className="btn-crm-info"
                  style={{
                    width: '100%',
                    marginTop: '1.9rem',
                    padding: '1.1rem',
                    fontSize: '1rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 9,
                  }}
                >
                  {t('crmdef.btn', 'Quiero esto para mi negocio')}
                  <ArrowRight size={18} />
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CRMExplanation;
